/*
 * Copyright © Siemens 2026
 */

import { Component, inject, OnDestroy, TemplateRef, ViewChild } from "@angular/core";
import { ModalService } from "@siemens/ix-angular/common";
import { IxButton, IxDropdownItem, IxIcon, IxModal, IxModalContent, IxModalFooter, IxModalHeader } from "@siemens/ix-angular/standalone";
import { AuthService } from "../../core/auth/services/auth.service";

@Component({
    selector: 'logout-modal',
    templateUrl: './logout-modal.html',
    imports: [
    IxModal,
    IxModalHeader,
    IxModalContent,
    IxModalFooter,
    IxButton,
    IxDropdownItem,
    ]
})
export class LogoutModal implements OnDestroy {
  @ViewChild('customModal', { read: TemplateRef })
  customModalRef!: TemplateRef<any>;
  private readonly modalService = inject(ModalService)
  private readonly authService = inject(AuthService)

  private closeSubscription?: { dispose(): void };
  private dismissSubscription?: { dispose(): void };
  private isOpen = false;
  private destroyed = false;
  private modalInstance?: Awaited<ReturnType<ModalService['open']>>;

  async openModal() {
    if (this.isOpen) return;
    this.isOpen = true;

    try {
      const instance = await this.modalService.open({
        content: this.customModalRef,
      });

      if (this.destroyed) {
        this.isOpen = false;
        await instance.htmlElement.dismissModal();
        return;
      }

      this.modalInstance = instance;
      this.closeSubscription = instance.onClose.on((result) => {
        this.disposeSubscriptions();
        this.isOpen = false;
        if (result === 'okay') {
          this.authService.logout();
        }
      });
      this.dismissSubscription = instance.onDismiss.on(() => {
        this.disposeSubscriptions();
        this.isOpen = false;
      });
    } catch (error) {
      this.isOpen = false;
      throw error;
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    const openModal = this.modalInstance;
    this.disposeSubscriptions();
    // Without this the modal would stay on screen with no listeners attached.
    void openModal?.htmlElement.dismissModal();
  }

  private disposeSubscriptions() {
    this.modalInstance = undefined;
    this.closeSubscription?.dispose();
    this.closeSubscription = undefined;
    this.dismissSubscription?.dispose();
    this.dismissSubscription = undefined;
  }
}